import type { Event, EventStatus, Visibility } from '@/config/objects/events';
import { buildMediaUrl, extractMediaId } from '@/lib/media';
import {
  detectBrowserTimezone,
  fromDatetimeLocalValue,
  toast,
  toDatetimeLocalValue,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

/**
 * Essentials of an event — identity, media, and schedule. Branding lives on
 * the event's Branding tab (see `@/lib/eventBranding`), so it is deliberately
 * absent here.
 */
export interface EventFormData {
  name: string;
  description: string;
  templateId: string;
  coverImageId?: string;
  introVideoId?: string;
  startTime: string;
  endTime: string;
  timezone: string;
  status: EventStatus;
  visibility: Visibility;
}

const DEFAULT_FORM: EventFormData = {
  name: '',
  description: '',
  templateId: '',
  coverImageId: undefined,
  introVideoId: undefined,
  startTime: '',
  endTime: '',
  // Admin's browser zone is closer to "what they expect" than UTC.
  timezone: detectBrowserTimezone(),
  status: 'draft',
  visibility: 'private',
};

interface UseEventFormArgs {
  /** Event id in edit mode; undefined when creating. */
  itemId: string | undefined;
  /** Called after a successful create, with the new event id. */
  onCreated: (eventId: string) => void;
  /** Called when an event fails to load in edit mode. */
  onNotFound: () => void;
}

interface UseEventForm {
  isEdit: boolean;
  formData: EventFormData;
  errors: Record<string, string>;
  updateField: <K extends keyof EventFormData>(key: K, value: EventFormData[K]) => void;
  isLoading: boolean;
  formReady: boolean;
  lastSaved: Date | null;
  isSaving: boolean;
  save: () => void;
}

/**
 * Form state + persistence for the event essentials form (create & edit).
 *
 * Keeps the page component presentational: it owns loading, hydration,
 * validation, and the create/update mutation. Timezone (de)serialization is
 * handled here so the page only binds plain wall-clock strings.
 *
 * @param args - Edit-mode id plus create/not-found navigation callbacks.
 * @returns Form state and actions.
 */
export function useEventForm({ itemId, onCreated, onNotFound }: UseEventFormArgs): UseEventForm {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const isEdit = !!itemId;

  const [formData, setFormData] = useState<EventFormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formReady, setFormReady] = useState(!isEdit);

  const updateField = useCallback(
    <K extends keyof EventFormData>(key: K, value: EventFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key as string]) return prev;
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    },
    [],
  );

  const {
    data: eventResponse,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: Event }>({
    queryKey: ['Event', itemId],
    queryFn: () => apiClient.get(`/events/${itemId}`),
    enabled: !!itemId,
  });

  useEffect(() => {
    if (!eventResponse?.data) return;
    const e = eventResponse.data;
    const tz = e.timezone || detectBrowserTimezone();
    setFormData({
      name: e.name ?? '',
      description: e.description ?? '',
      templateId: e.templateId ?? '',
      coverImageId: extractMediaId(e.coverImageUrl),
      introVideoId: extractMediaId(e.introVideoUrl),
      // Render the stored UTC instant in the event's timezone, not the admin's
      // browser zone, so a 2pm America/New_York event shows "14:00" anywhere.
      startTime: toDatetimeLocalValue(e.startTime, tz),
      endTime: toDatetimeLocalValue(e.endTime, tz),
      timezone: tz,
      status: e.status ?? 'draft',
      visibility: e.visibility ?? 'private',
    });
    setLastSaved(new Date(e.updatedAt));
    setFormReady(true);
  }, [eventResponse]);

  useEffect(() => {
    if (isError) onNotFound();
  }, [isError, onNotFound]);

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEdit) {
        return apiClient.put<{ data: Event }>(`/events/${itemId}`, payload);
      }
      return apiClient.post<{ data: Event }>('/events', payload);
    },
    onSuccess: (response: { data: Event }) => {
      queryClient.invalidateQueries({ queryKey: ['Event'] });
      setLastSaved(new Date());
      toast.success(isEdit ? 'Event saved successfully' : 'Event created successfully');
      if (isEdit) return;
      onCreated(response.data.id);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save event');
    },
  });

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    else if (formData.name.trim().length < 2) errs.name = 'Name must be at least 2 characters';

    // Both values are same-shaped strings interpreted in the same zone, so a
    // direct string compare orders them correctly.
    if (formData.startTime && formData.endTime && formData.endTime <= formData.startTime) {
      errs.endTime = 'End time must be after start time';
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      document.getElementById(`field-${firstKey}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return false;
    }
    return true;
  }, [formData]);

  const save = useCallback(() => {
    if (!validate()) return;
    saveMutation.mutate({
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      templateId: formData.templateId || null,
      coverImageUrl: buildMediaUrl(formData.coverImageId),
      introVideoUrl: buildMediaUrl(formData.introVideoId),
      // The user typed wall-clock times in the event's timezone — serialise to
      // UTC through that lens so the round-trip stays lossless.
      startTime: fromDatetimeLocalValue(formData.startTime, formData.timezone),
      endTime: fromDatetimeLocalValue(formData.endTime, formData.timezone),
      timezone: formData.timezone,
      status: formData.status,
      visibility: formData.visibility,
    });
  }, [formData, saveMutation, validate]);

  return {
    isEdit,
    formData,
    errors,
    updateField,
    isLoading,
    formReady,
    lastSaved,
    isSaving: saveMutation.isPending,
    save,
  };
}
